import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { logDebug } from '../utils/utils';

/**
 * Context for sharing real-time form values across components
 * This allows components outside of Formik context to access current form values
 * without waiting for form submission or Redux updates
 */

interface FormValuesContextType {
    formValues: Record<string, unknown>;
    /** draftPaths: number fields whose text is not a number yet by the rule of their own field. */
    updateFormValues: (values: Record<string, unknown>, draftPaths?: ReadonlySet<string>) => void;
    getFieldValue: (fieldPath: string) => unknown;
}

const FormValuesContext = createContext<FormValuesContextType | undefined>(undefined);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) return false;
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
};

/**
 * Formik keeps the text of a number field that is not a number yet (`0,`, `1,000`) so that Yup can show its error;
 * the readers of this context use the values as numbers, so such a field keeps the last number it published.
 */
const keepNumbersOverDrafts = (next: unknown, published: unknown, draftPaths: ReadonlySet<string>, path = ''): unknown => {
    if (draftPaths.has(path) && typeof published === 'number' && Number.isFinite(published)) return published;
    if (isPlainObject(next)) {
        return Object.fromEntries(
            Object.entries(next).map(([key, value]) => [key, keepNumbersOverDrafts(value, isPlainObject(published) ? published[key] : undefined, draftPaths, path ? `${path}.${key}` : key)])
        );
    }
    return next;
};

interface FormValuesProviderProps {
    children: ReactNode;
}

export const FormValuesProvider: React.FC<FormValuesProviderProps> = ({ children }) => {
    const [formValues, setFormValues] = useState<Record<string, unknown>>({});

    const updateFormValues = useCallback((values: Record<string, unknown>, draftPaths: ReadonlySet<string> = new Set()) => {
        setFormValues(prevValues => {
            const publishedValues = keepNumbersOverDrafts(values, prevValues, draftPaths) as Record<string, unknown>;
            // Only update if values have actually changed to prevent unnecessary re-renders
            const hasChanged = JSON.stringify(prevValues) !== JSON.stringify(publishedValues);
            if (hasChanged) {
                logDebug('[FormValuesContext]', 'Form values updated', publishedValues);
                return { ...publishedValues };
            }
            return prevValues;
        });
    }, []);

    /**
     * Get a nested field value using dot notation (e.g., "railway.setStabilisedBallastThickness")
     */
    const getFieldValue = useCallback(
        (fieldPath: string) => {
            const keys = fieldPath.split('.');
            let value: unknown = formValues;

            for (const key of keys) {
                value = (value as Record<string, unknown>)?.[key];
                if (value === undefined) return undefined;
            }

            return value;
        },
        [formValues]
    );

    const contextValue: FormValuesContextType = {
        formValues,
        updateFormValues,
        getFieldValue,
    };

    return <FormValuesContext.Provider value={contextValue}>{children}</FormValuesContext.Provider>;
};

/**
 * Hook to access form values context
 * Throws error if used outside of FormValuesProvider
 */
export const useFormValues = (): FormValuesContextType => {
    const context = useContext(FormValuesContext);
    if (context === undefined) {
        throw new Error('useFormValues must be used within a FormValuesProvider');
    }
    return context;
};

/**
 * Hook to get a specific field value with real-time updates
 * @param fieldPath - Dot notation path to the field (e.g., "railway.setStabilisedBallastThickness")
 * @param defaultValue - Default value if field doesn't exist
 */
export const useFormFieldValue = <T = unknown,>(fieldPath: string, defaultValue?: T): T | undefined => {
    const { getFieldValue } = useFormValues();
    const value = getFieldValue(fieldPath);
    return value !== undefined ? (value as T) : defaultValue;
};
